import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/api';
import { Plus, Save, X, Eye, Edit, Trash2, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

// Shared Scoring / AI Prompt / Usage Limits tabs, used by both InterviewManagement and
// PitchManagement. Parameterized by:
//   scoringBase  e.g. '/admin/interview-scoring/categories' | '/admin/pitch-scoring/categories'
//   featureKey   e.g. 'mock_interview' | 'elevator_pitch'  (for /admin/prompt-config & /admin/feature-limits)
//   scoringType  'general' | 'star' (ScoringTab only) -- InterviewScoringCategories holds both
//                rubrics; a feature with no STAR-style split (elevator_pitch, etc.) just omits
//                this prop and gets the 'general' default, same as before this existed.

const SCORING_EMPTY_FORM = { name: '', weight: '', description: '', rubric: '', sortOrder: '' };

function tabStyles(isDarkMode) {
    return {
        card: `rounded-2xl border shadow-sm ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`,
        input: `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow ${isDarkMode ? 'bg-slate-900 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-300'}`,
        label: `block text-xs font-semibold mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`,
        btnPrimary: 'px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium transition-colors flex items-center gap-2',
        btnSecondary: `px-4 py-2 border rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${isDarkMode ? 'border-slate-600 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'}`,
    };
}

export function ScoringTab({ isDarkMode, scoringBase, blurb, scoringType = 'general' }) {
    const s = tabStyles(isDarkMode);
    const [categories, setCategories] = useState([]);
    const [statusTab, setStatusTab] = useState('active');
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    const [newItem, setNewItem] = useState(SCORING_EMPTY_FORM);
    const [showNewForm, setShowNewForm] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(scoringBase, { params: { scoringType } });
            setCategories(res.data.categories || []);
        } catch (err) {
            console.error('Error fetching scoring categories:', err);
        } finally {
            setLoading(false);
        }
    }, [scoringBase, scoringType]);

    useEffect(() => { fetchCategories(); }, [fetchCategories]);

    const handleCreate = async () => {
        if (!newItem.name.trim()) return;
        const weight = parseInt(newItem.weight, 10);
        if (isNaN(weight) || weight < 0) return alert('Weight must be a non-negative number');
        setSaving(true);
        try {
            await api.post(scoringBase, {
                name: newItem.name, weight, description: newItem.description,
                rubric: newItem.rubric, sortOrder: parseInt(newItem.sortOrder, 10) || 0,
                scoringType,
            });
            setNewItem(SCORING_EMPTY_FORM);
            setShowNewForm(false);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create category');
        } finally { setSaving(false); }
    };

    const handleUpdate = async (item) => {
        const weight = parseInt(item.Weight, 10);
        if (isNaN(weight) || weight < 0) return alert('Weight must be a non-negative number');
        setSaving(true);
        try {
            await api.put(`${scoringBase}/${item.CategoryID}`, {
                name: item.Name, weight, description: item.Description || '',
                rubric: item.Rubric || '', sortOrder: parseInt(item.SortOrder, 10) || 0,
            });
            setEditing(null);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update category');
        } finally { setSaving(false); }
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Deactivate "${item.Name}"?`)) return;
        setSaving(true);
        try {
            await api.delete(`${scoringBase}/${item.CategoryID}`);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to delete category');
        } finally { setSaving(false); }
    };

    const handleRestore = async (item) => {
        setSaving(true);
        try {
            await api.patch(`${scoringBase}/${item.CategoryID}/restore`);
            await fetchCategories();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to restore category');
        } finally { setSaving(false); }
    };

    const activeCategories = categories.filter((c) => c.isActive !== false && c.isActive !== 0);
    const inactiveCategories = categories.filter((c) => c.isActive === false || c.isActive === 0);
    const currentList = statusTab === 'active' ? activeCategories : inactiveCategories;
    const totalWeight = activeCategories.reduce((sum, c) => sum + (c.Weight || 0), 0);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
            </div>
        );
    }

    return (
        <div>
            <p className={`mb-4 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                {blurb}
            </p>

            <div className={s.card}>
                <div className="flex items-center justify-between px-5 pt-4 pb-3">
                    <div className="flex gap-2">
                        {[
                            { key: 'active', label: 'Active', items: activeCategories },
                            { key: 'inactive', label: 'Inactive', items: inactiveCategories },
                        ].map((st) => (
                            <button
                                key={st.key}
                                onClick={() => { setStatusTab(st.key); setEditing(null); }}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${
                                    statusTab === st.key
                                        ? (st.key === 'inactive' ? 'bg-slate-700 text-white' : 'bg-primary-600 text-white')
                                        : isDarkMode ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                }`}
                            >
                                {st.label} ({st.items.length})
                            </button>
                        ))}
                    </div>
                    <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${
                            totalWeight === 100 ? 'bg-primary-100 text-primary-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                            Total Weight: {totalWeight}%{totalWeight !== 100 ? ' (should be 100%)' : ''}
                        </span>
                        {statusTab === 'active' && (
                            <button onClick={() => setShowNewForm(!showNewForm)} className={s.btnPrimary}>
                                <Plus size={16} /> Add Category
                            </button>
                        )}
                    </div>
                </div>

                {showNewForm && statusTab === 'active' && (
                    <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                        <div className="grid grid-cols-12 gap-3 mb-3">
                            <div className="col-span-4">
                                <label className={s.label}>Name</label>
                                <input type="text" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} className={s.input} placeholder="Category name" />
                            </div>
                            <div className="col-span-2">
                                <label className={s.label}>Weight %</label>
                                <input type="number" min="0" max="100" value={newItem.weight} onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })} className={s.input} />
                            </div>
                            <div className="col-span-5">
                                <label className={s.label}>Description</label>
                                <input type="text" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} className={s.input} placeholder="What does this category evaluate?" />
                            </div>
                            <div className="col-span-1">
                                <label className={s.label}>Sort</label>
                                <input type="number" min="0" value={newItem.sortOrder} onChange={(e) => setNewItem({ ...newItem, sortOrder: e.target.value })} className={s.input} />
                            </div>
                        </div>
                        <div className="mb-3">
                            <label className={s.label}>Grading Rubric</label>
                            <textarea value={newItem.rubric} onChange={(e) => setNewItem({ ...newItem, rubric: e.target.value })} rows={5} className={s.input} placeholder="A (85-100): ...&#10;B (65-84): ...&#10;C (45-64): ...&#10;D (25-44): ...&#10;F (0-24): ..." />
                        </div>
                        <div className="flex gap-2">
                            <button onClick={handleCreate} disabled={saving} className={s.btnPrimary}><Save size={16} /> Save</button>
                            <button onClick={() => { setShowNewForm(false); setNewItem(SCORING_EMPTY_FORM); }} className={s.btnSecondary}><X size={16} /> Cancel</button>
                        </div>
                    </div>
                )}

                <div className="px-5 pb-5">
                    {currentList.length === 0 ? (
                        <p className={`text-center py-12 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            {statusTab === 'inactive' ? 'No inactive categories.' : 'No categories found.'}
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {currentList.map((item) => (
                                <div key={item.CategoryID} className={`border rounded-xl transition-all ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                    {editing?.CategoryID === item.CategoryID ? (
                                        <div className="p-4">
                                            <div className="grid grid-cols-12 gap-3 mb-3">
                                                <div className="col-span-4">
                                                    <label className={s.label}>Name</label>
                                                    <input type="text" value={editing.Name} onChange={(e) => setEditing({ ...editing, Name: e.target.value })} className={s.input} />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className={s.label}>Weight %</label>
                                                    <input type="number" min="0" max="100" value={editing.Weight} onChange={(e) => setEditing({ ...editing, Weight: e.target.value })} className={s.input} />
                                                </div>
                                                <div className="col-span-5">
                                                    <label className={s.label}>Description</label>
                                                    <input type="text" value={editing.Description || ''} onChange={(e) => setEditing({ ...editing, Description: e.target.value })} className={s.input} />
                                                </div>
                                                <div className="col-span-1">
                                                    <label className={s.label}>Sort</label>
                                                    <input type="number" min="0" value={editing.SortOrder} onChange={(e) => setEditing({ ...editing, SortOrder: e.target.value })} className={s.input} />
                                                </div>
                                            </div>
                                            <div className="mb-3">
                                                <label className={s.label}>Grading Rubric</label>
                                                <textarea value={editing.Rubric || ''} onChange={(e) => setEditing({ ...editing, Rubric: e.target.value })} rows={6} className={s.input} />
                                            </div>
                                            <div className="flex gap-2">
                                                <button onClick={() => handleUpdate(editing)} disabled={saving} className={s.btnPrimary}><Save size={16} /> Save</button>
                                                <button onClick={() => setEditing(null)} className={s.btnSecondary}><X size={16} /> Cancel</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="p-4">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-3 flex-1">
                                                    <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.Name}</span>
                                                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-primary-100 text-primary-700'}`}>
                                                        {item.Weight}%
                                                    </span>
                                                    {item.Description && (
                                                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>— {item.Description}</span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {item.Rubric && (
                                                        <button
                                                            onClick={() => setExpandedId(expandedId === item.CategoryID ? null : item.CategoryID)}
                                                            className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}
                                                            title="View rubric"
                                                        >
                                                            {expandedId === item.CategoryID ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                                        </button>
                                                    )}
                                                    <button onClick={() => setEditing({ ...item })} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                                        <Edit size={16} />
                                                    </button>
                                                    {statusTab === 'active' ? (
                                                        <button onClick={() => handleDelete(item)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Deactivate">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    ) : (
                                                        <button onClick={() => handleRestore(item)} className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors" title="Restore">
                                                            <RotateCcw size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            {expandedId === item.CategoryID && item.Rubric && (
                                                <pre className={`mt-3 p-3 rounded-lg text-sm whitespace-pre-wrap font-sans ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-slate-50 text-slate-600'}`}>
                                                    {item.Rubric}
                                                </pre>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export function PromptTab({ isDarkMode, featureKey }) {
    const s = tabStyles(isDarkMode);
    const [prompts, setPrompts] = useState([]);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [newPrompt, setNewPrompt] = useState({ featureName: featureKey, promptText: '' });
    const [showNewPrompt, setShowNewPrompt] = useState(false);
    const [previewResult, setPreviewResult] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchPrompts = useCallback(async () => {
        try {
            const res = await api.get('/admin/prompt-config', { params: { feature: featureKey } });
            setPrompts(res.data.configs || []);
        } catch (err) {
            console.error('Error fetching prompts:', err);
        }
    }, [featureKey]);

    useEffect(() => { fetchPrompts(); }, [fetchPrompts]);

    const handleCreatePrompt = async () => {
        if (!newPrompt.promptText.trim()) return;
        setSaving(true);
        try {
            await api.post('/admin/prompt-config', {
                featureName: newPrompt.featureName, promptText: newPrompt.promptText,
            });
            setNewPrompt({ featureName: featureKey, promptText: '' });
            setShowNewPrompt(false);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create prompt');
        } finally { setSaving(false); }
    };

    const handleUpdatePrompt = async (item) => {
        setSaving(true);
        try {
            await api.put(`/admin/prompt-config/${item.PromptConfigID}`, { promptText: item.PromptText });
            setEditingPrompt(null);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update prompt');
        } finally { setSaving(false); }
    };

    const handlePublishPrompt = async (id) => {
        if (!window.confirm('Publish this prompt? It will become the active prompt for this feature.')) return;
        setSaving(true);
        try {
            await api.post(`/admin/prompt-config/${id}/publish`);
            await fetchPrompts();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to publish prompt');
        } finally { setSaving(false); }
    };

    const handlePreviewPrompt = async (id) => {
        setPreviewing(true);
        try {
            const res = await api.post(`/admin/prompt-config/${id}/test`);
            setPreviewResult(res.data);
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to preview prompt');
        } finally { setPreviewing(false); }
    };

    return (
        <div className={s.card}>
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>AI Prompt Templates</h2>
                <button onClick={() => setShowNewPrompt(!showNewPrompt)} className={s.btnPrimary}>
                    <Plus size={16} /> New Version
                </button>
            </div>

            <p className={`px-5 pb-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Use <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{'{categories_block}'}</code> and <code className={`px-1 py-0.5 rounded text-xs ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>{'{num_categories}'}</code> as placeholders. Categories, weights, and rubrics are injected automatically from the Scoring tab.
                {['mock_interview', 'star_analysis', 'elevator_pitch'].includes(featureKey) &&' The JSON output format is fixed and added automatically after your prompt (shown in Preview), so do not include one here.'}
            </p>

            {showNewPrompt && (
                <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="mb-3">
                        <label className={s.label}>Feature</label>
                        <input type="text" value={newPrompt.featureName} readOnly className={`${s.input} cursor-not-allowed opacity-70`} />
                    </div>
                    <div className="mb-3">
                        <label className={s.label}>Prompt Template</label>
                        <textarea value={newPrompt.promptText} onChange={(e) => setNewPrompt({ ...newPrompt, promptText: e.target.value })} rows={12} className={`${s.input} font-mono text-xs`} placeholder="Enter your prompt template..." />
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleCreatePrompt} disabled={saving} className={s.btnPrimary}><Save size={16} /> Save Draft</button>
                        <button onClick={() => { setShowNewPrompt(false); setNewPrompt({ featureName: featureKey, promptText: '' }); }} className={s.btnSecondary}><X size={16} /> Cancel</button>
                    </div>
                </div>
            )}

            <div className="px-5 pb-5 space-y-3">
                {prompts.length === 0 ? (
                    <div className={`text-center py-12 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        <p className="text-sm mb-2">No custom prompts yet. The system is using the built-in default prompt.</p>
                        <p className="text-xs">Create a new version to customize the AI's behavior.</p>
                    </div>
                ) : (
                    prompts.map((p) => (
                        <div key={p.PromptConfigID} className={`border rounded-xl p-4 ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                            {editingPrompt?.PromptConfigID === p.PromptConfigID ? (
                                <>
                                    <div className="mb-3">
                                        <label className={s.label}>Prompt Template</label>
                                        <textarea value={editingPrompt.PromptText} onChange={(e) => setEditingPrompt({ ...editingPrompt, PromptText: e.target.value })} rows={14} className={`${s.input} font-mono text-xs`} />
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => handleUpdatePrompt(editingPrompt)} disabled={saving} className={s.btnPrimary}><Save size={16} /> Save</button>
                                        <button onClick={() => setEditingPrompt(null)} className={s.btnSecondary}><X size={16} /> Cancel</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-3">
                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{p.FeatureName}</span>
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>v{p.Version}</span>
                                            {p.IsPublished ? (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-semibold">Published</span>
                                            ) : (
                                                <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>Draft</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <button onClick={() => handlePreviewPrompt(p.PromptConfigID)} disabled={previewing} className={`p-2 rounded-lg transition-colors ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`} title="Preview rendered prompt">
                                                <Eye size={16} />
                                            </button>
                                            <button onClick={async () => {
                                                const res = await api.get(`/admin/prompt-config/${p.PromptConfigID}`);
                                                setEditingPrompt(res.data.prompt || res.data);
                                            }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                                <Edit size={16} />
                                            </button>
                                            {!p.IsPublished && (
                                                <button onClick={() => handlePublishPrompt(p.PromptConfigID)} className={s.btnPrimary + ' !px-3 !py-1.5 !text-xs'}>
                                                    Publish
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {p.PublishedOn && (
                                        <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                                            Published: {new Date(p.PublishedOn).toLocaleString()}
                                        </p>
                                    )}
                                </>
                            )}
                        </div>
                    ))
                )}

                {previewResult && (
                    <div className={`border rounded-xl p-4 mt-4 ${isDarkMode ? 'border-primary-800 bg-primary-900/20' : 'border-primary-200 bg-primary-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <h3 className={`text-sm font-semibold ${isDarkMode ? 'text-primary-400' : 'text-primary-700'}`}>Rendered Preview</h3>
                            <button onClick={() => setPreviewResult(null)} className={`p-1 rounded ${isDarkMode ? 'text-slate-400 hover:bg-slate-700' : 'text-slate-400 hover:bg-slate-100'}`}><X size={16} /></button>
                        </div>
                        <p className={`text-xs mb-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            {previewResult.categoryCount} categories &middot; {previewResult.charCount} characters
                        </p>
                        <pre className={`p-3 rounded-lg text-xs whitespace-pre-wrap font-mono max-h-96 overflow-y-auto ${isDarkMode ? 'bg-slate-900 text-slate-300' : 'bg-white text-slate-700 border border-slate-200'}`}>
                            {previewResult.rendered}
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
}

export function LimitsTab({ isDarkMode, featureKey, blurb }) {
    const s = tabStyles(isDarkMode);
    const [limits, setLimits] = useState([]);
    const [editingLimit, setEditingLimit] = useState(null);
    const [newLimit, setNewLimit] = useState({ tierID: '', featureName: featureKey, monthlyLimit: '' });
    const [showNewLimit, setShowNewLimit] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchLimits = useCallback(async () => {
        try {
            const res = await api.get('/admin/feature-limits', { params: { feature: featureKey } });
            setLimits(res.data.limits || []);
        } catch (err) {
            console.error('Error fetching limits:', err);
        }
    }, [featureKey]);

    useEffect(() => { fetchLimits(); }, [fetchLimits]);

    const handleCreateLimit = async () => {
        const tierID = parseInt(newLimit.tierID, 10);
        const monthlyLimit = parseInt(newLimit.monthlyLimit, 10);
        if (isNaN(tierID) || isNaN(monthlyLimit)) return alert('Tier ID and Monthly Limit must be numbers');
        setSaving(true);
        try {
            await api.post('/admin/feature-limits', {
                tierID, featureName: newLimit.featureName, monthlyLimit,
            });
            setNewLimit({ tierID: '', featureName: featureKey, monthlyLimit: '' });
            setShowNewLimit(false);
            await fetchLimits();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to create limit');
        } finally { setSaving(false); }
    };

    const handleUpdateLimit = async (item) => {
        const monthlyLimit = parseInt(item.MonthlyLimit, 10);
        if (isNaN(monthlyLimit)) return alert('Monthly limit must be a number');
        setSaving(true);
        try {
            await api.put(`/admin/feature-limits/${item.FeatureLimitID}`, { monthlyLimit });
            setEditingLimit(null);
            await fetchLimits();
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to update limit');
        } finally { setSaving(false); }
    };

    return (
        <div className={s.card}>
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
                <h2 className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>Usage Limits</h2>
                <button onClick={() => setShowNewLimit(!showNewLimit)} className={s.btnPrimary}>
                    <Plus size={16} /> Add Limit
                </button>
            </div>

            <p className={`px-5 pb-3 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                {blurb}
            </p>

            {showNewLimit && (
                <div className={`mx-5 mb-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-600 bg-slate-900' : 'border-slate-200 bg-slate-50'}`}>
                    <div className="grid grid-cols-3 gap-3 mb-3">
                        <div>
                            <label className={s.label}>Tier ID</label>
                            <input type="number" value={newLimit.tierID} onChange={(e) => setNewLimit({ ...newLimit, tierID: e.target.value })} className={s.input} placeholder="e.g. 1" />
                        </div>
                        <div>
                            <label className={s.label}>Feature</label>
                            <input type="text" value={newLimit.featureName} readOnly className={`${s.input} cursor-not-allowed opacity-70`} />
                        </div>
                        <div>
                            <label className={s.label}>Monthly Limit</label>
                            <input type="number" value={newLimit.monthlyLimit} onChange={(e) => setNewLimit({ ...newLimit, monthlyLimit: e.target.value })} className={s.input} placeholder="e.g. 30" />
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleCreateLimit} disabled={saving} className={s.btnPrimary}><Save size={16} /> Save</button>
                        <button onClick={() => setShowNewLimit(false)} className={s.btnSecondary}><X size={16} /> Cancel</button>
                    </div>
                </div>
            )}

            <div className="px-5 pb-5">
                {limits.length === 0 ? (
                    <p className={`text-center py-12 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        No usage limits configured. Add one to start.
                    </p>
                ) : (
                    <div className="space-y-2">
                        {limits.map((item) => (
                            <div key={item.FeatureLimitID} className={`flex items-center gap-4 p-4 border rounded-xl ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                                {editingLimit?.FeatureLimitID === item.FeatureLimitID ? (
                                    <>
                                        <div className="flex-1">
                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.TierName || `Tier ${item.TierID}`}</span>
                                            <span className={`ml-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.FeatureName}</span>
                                        </div>
                                        <input
                                            type="number" min="0" value={editingLimit.MonthlyLimit}
                                            onChange={(e) => setEditingLimit({ ...editingLimit, MonthlyLimit: e.target.value })}
                                            className={`w-24 ${s.input}`}
                                        />
                                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>/ month</span>
                                        <button onClick={() => handleUpdateLimit(editingLimit)} disabled={saving} className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"><Save size={16} /></button>
                                        <button onClick={() => setEditingLimit(null)} className="p-2 text-slate-500 hover:bg-slate-100 rounded-lg"><X size={16} /></button>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex-1">
                                            <span className={`font-medium ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.TierName || `Tier ${item.TierID}`}</span>
                                            <span className={`ml-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.FeatureName}</span>
                                        </div>
                                        <span className={`text-lg font-semibold ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>{item.MonthlyLimit}</span>
                                        <span className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>/ month</span>
                                        <button onClick={() => setEditingLimit({ ...item })} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                                            <Edit size={16} />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
